import React from 'react';
import {
  ChevronDown,
  Copy,
  Dumbbell,
  Edit,
  Filter,
  MoreVertical,
  Search,
  Trash2,
} from '../../../bravosIcons';
import type { LibraryWorkoutsApi } from '../../hooks/useLibraryWorkouts';
import type { WorkoutSortColumn } from '../../types/library';
import { getWorkoutExerciseCount, isTrainerOwnedLibraryRow } from '../../utils/workoutLibraryRowUtils';
import {
  WorkoutSortIcon,
  clearWorkoutFilter,
  hasWorkoutFilter,
} from './workoutMobileTableHelpers';

export type WorkoutsTableMobileProps = {
  wk: LibraryWorkoutsApi;
  workouts: any[];
  profileId: string | undefined;
  selectedWorkouts: Set<string>;
  openWorkoutMenu: string | null;
  setOpenWorkoutMenu: (id: string | null) => void;
  openWorkoutFilterColumn: WorkoutSortColumn | null;
  setOpenWorkoutFilterColumn: (column: WorkoutSortColumn | null) => void;
  workoutFilterRefs: {
    name: React.RefObject<HTMLDivElement>;
    createdAt: React.RefObject<HTMLDivElement>;
    exercises: React.RefObject<HTMLDivElement>;
  };
  workoutMenuRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  trainerWorkoutDuplicateInFlight: boolean;
  onToggleWorkoutFilter: (column: WorkoutSortColumn, e: React.MouseEvent) => void;
  onSelectAllWorkouts: () => void;
  onSelectWorkout: (workoutId: string) => void;
  onWorkoutClick: (workout: any, e?: React.MouseEvent) => void;
  onEditWorkout: (workout: any) => void;
  onDuplicateWorkout: (workout: any) => void;
  onDeleteWorkout: (workoutId: string, workoutName: string) => void;
};

/** Mobile Library — Workouts tab horizontal table (R7). */
export function WorkoutsTableMobile({
  wk,
  workouts,
  profileId,
  selectedWorkouts,
  openWorkoutMenu,
  setOpenWorkoutMenu,
  openWorkoutFilterColumn,
  setOpenWorkoutFilterColumn,
  workoutFilterRefs,
  workoutMenuRefs,
  trainerWorkoutDuplicateInFlight,
  onToggleWorkoutFilter,
  onSelectAllWorkouts,
  onSelectWorkout,
  onWorkoutClick,
  onEditWorkout,
  onDuplicateWorkout,
  onDeleteWorkout,
}: WorkoutsTableMobileProps) {
  return (
                          <div className="relative">
                            <div 
                              className="overflow-x-auto"
                              style={{ 
                                WebkitOverflowScrolling: 'touch',
                                width: '100%',
                                maxWidth: '100vw'
                              }}
                            >
                              <div style={{ width: 'max-content', minWidth: '100%' }}>
                                <table 
                                  className="w-full border-collapse" 
                                  style={{ 
                                    minWidth: '900px',
                                    width: '900px'
                                  }}
                                >
                                <thead>
                                  <tr className="bg-white dark:bg-theme-surface">
                                    <th className="px-4 md:px-6 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-theme-off-white/60 w-12">
                                      <input
                                        type="checkbox"
                                        checked={wk.filteredAndSortedWorkouts.length > 0 && selectedWorkouts.size === wk.filteredAndSortedWorkouts.length}
                                        onChange={onSelectAllWorkouts}
                                        className="rounded border-theme-border dark:border-white/10 text-accent-violet dark:text-accent-lime focus:ring-accent-violet dark:focus:ring-accent-lime"
                                      />
                                    </th>
                                    {/* Name Column Header */}
                                    <th 
                                      className="px-4 md:px-6 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-theme-off-white/60 cursor-pointer hover:bg-theme-muted dark:hover:bg-white/10 group relative"
                                      onClick={(e) => wk.handleWorkoutSort('name', e)}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span>Name</span>
                                        <div className="flex items-center gap-1">
                                          <button
                                            className="filter-trigger p-1 hover:bg-theme-muted dark:hover:bg-white/10 rounded"
                                            onClick={(e) => onToggleWorkoutFilter('name', e)}
                                            title="Filter"
                                          >
                                            <Filter className={`w-3 h-3 ${hasWorkoutFilter(wk, 'name') ? 'text-accent-violet dark:text-accent-lime' : 'text-theme-text/50 dark:text-theme-off-white/50'}`} />
                                          </button>
                                          <WorkoutSortIcon wk={wk} column="name" />
                                        </div>
                                      </div>
                                      {openWorkoutFilterColumn === 'name' && (
                                        <>
                                          <div 
                                            className="fixed inset-0 z-[45]"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenWorkoutFilterColumn(null);
                                            }}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            style={{ touchAction: 'none' }}
                                          />
                                          <div 
                                            ref={workoutFilterRefs.name}
                                            className="filter-dropdown absolute top-full left-0 mt-1 w-64 bg-theme-bg dark:bg-theme-surface border border-theme-border dark:border-white/10 rounded-md shadow-lg z-[50] p-3"
                                            onClick={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                          >
                                            <div className="mb-2 flex items-center justify-between">
                                              <label className="text-xs font-medium text-theme-text dark:text-theme-off-white">Search by name</label>
                                              {hasWorkoutFilter(wk, 'name') && (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    clearWorkoutFilter(wk, 'name');
                                                  }}
                                                  onTouchEnd={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    clearWorkoutFilter(wk, 'name');
                                                  }}
                                                  className="text-xs text-blue-600 hover:text-blue-700"
                                                  style={{ touchAction: 'manipulation' }}
                                                >
                                                  Clear
                                                </button>
                                              )}
                                            </div>
                                            <div className="relative">
                                              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                              <input
                                                type="text"
                                                value={wk.workoutNameFilter}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  wk.setWorkoutNameFilter(e.target.value);
                                                }}
                                                onFocus={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                                onTouchStart={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                placeholder="Search workouts..."
                                                className="w-full pl-8 pr-2 py-1.5 border border-theme-border dark:border-white/10 rounded text-sm text-theme-text dark:text-theme-off-white bg-theme-bg dark:bg-theme-surface focus:outline-none focus:ring-2 focus:ring-accent-violet dark:focus:ring-accent-lime focus:border-accent-violet dark:focus:border-accent-lime"
                                                autoFocus
                                              />
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </th>

                                    {/* Created Column Header */}
                                    <th 
                                      className="px-4 md:px-6 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-theme-off-white/60 cursor-pointer hover:bg-theme-muted dark:hover:bg-white/10 group relative"
                                      onClick={(e) => wk.handleWorkoutSort('createdAt', e)}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span>Created</span>
                                        <div className="flex items-center gap-1">
                                          <button
                                            className="filter-trigger p-1 hover:bg-theme-muted dark:hover:bg-white/10 rounded"
                                            onClick={(e) => onToggleWorkoutFilter('createdAt', e)}
                                            title="Filter"
                                          >
                                            <Filter className={`w-3 h-3 ${hasWorkoutFilter(wk, 'createdAt') ? 'text-accent-violet dark:text-accent-lime' : 'text-theme-text/50 dark:text-theme-off-white/50'}`} />
                                          </button>
                                          <WorkoutSortIcon wk={wk} column="createdAt" />
                                        </div>
                                      </div>
                                      {openWorkoutFilterColumn === 'createdAt' && (
                                        <>
                                          <div 
                                            className="fixed inset-0 z-[45]"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenWorkoutFilterColumn(null);
                                            }}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            style={{ touchAction: 'none' }}
                                          />
                                          <div 
                                            ref={workoutFilterRefs.createdAt}
                                            className="filter-dropdown absolute top-full right-0 mt-1 w-72 bg-theme-bg dark:bg-theme-surface border border-theme-border dark:border-white/10 rounded-md shadow-lg z-[50] p-3"
                                            onClick={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                          >
                                            <div className="mb-2 flex items-center justify-between">
                                              <label className="text-xs font-medium text-theme-text dark:text-theme-off-white">Filter by date</label>
                                              {hasWorkoutFilter(wk, 'createdAt') && (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    clearWorkoutFilter(wk, 'createdAt');
                                                  }}
                                                  onTouchEnd={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    clearWorkoutFilter(wk, 'createdAt');
                                                  }}
                                                  className="text-xs text-blue-600 hover:text-blue-700"
                                                  style={{ touchAction: 'manipulation' }}
                                                >
                                                  Clear
                                                </button>
                                              )}
                                            </div>
                                            
                                            <div className="mb-3">
                                              <select
                                                value={wk.workoutDateFilterType}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  wk.setWorkoutDateFilterType(e.target.value as 'exact' | 'before' | 'after' | 'between');
                                                  if (e.target.value !== 'between') {
                                                    wk.setWorkoutDateFilterEnd('');
                                                  }
                                                }}
                                                onFocus={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                                onTouchStart={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                className="w-full px-2 py-1.5 border border-theme-border dark:border-white/10 rounded text-sm text-theme-text dark:text-theme-off-white bg-theme-bg dark:bg-theme-surface focus:outline-none focus:ring-2 focus:ring-accent-violet dark:focus:ring-accent-lime focus:border-accent-violet dark:focus:border-accent-lime"
                                              >
                                                <option value="exact">On</option>
                                                <option value="before">Before</option>
                                                <option value="after">After</option>
                                                <option value="between">Between</option>
                                              </select>
                                            </div>

                                            <div className="mb-2">
                                              <label className="block text-xs text-theme-text/70 dark:text-theme-off-white/70 mb-1">
                                                {wk.workoutDateFilterType === 'between' ? 'Start date' : 'Date'}
                                              </label>
                                              <input
                                                type="date"
                                                value={wk.workoutDateFilter}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  wk.setWorkoutDateFilter(e.target.value);
                                                }}
                                                onFocus={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                                onTouchStart={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                className="w-full px-2 py-1.5 border border-theme-border dark:border-white/10 rounded text-sm text-theme-text dark:text-theme-off-white bg-theme-bg dark:bg-theme-surface focus:outline-none focus:ring-2 focus:ring-accent-violet dark:focus:ring-accent-lime focus:border-accent-violet dark:focus:border-accent-lime"
                                              />
                                            </div>

                                            {wk.workoutDateFilterType === 'between' && (
                                              <div>
                                                <label className="block text-xs text-theme-text/70 dark:text-theme-off-white/70 mb-1">End date</label>
                                                <input
                                                  type="date"
                                                  value={wk.workoutDateFilterEnd}
                                                  onChange={(e) => {
                                                    e.stopPropagation();
                                                    wk.setWorkoutDateFilterEnd(e.target.value);
                                                  }}
                                                  onFocus={(e) => e.stopPropagation()}
                                                  onClick={(e) => e.stopPropagation()}
                                                  onTouchStart={(e) => e.stopPropagation()}
                                                  onMouseDown={(e) => e.stopPropagation()}
                                                  className="w-full px-2 py-1.5 border border-theme-border dark:border-white/10 rounded text-sm text-theme-text dark:text-theme-off-white bg-theme-bg dark:bg-theme-surface focus:outline-none focus:ring-2 focus:ring-accent-violet dark:focus:ring-accent-lime focus:border-accent-violet dark:focus:border-accent-lime"
                                                />
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      )}
                                    </th>

                                    {/* Exercises Column Header */}
                                    <th 
                                      className="px-4 md:px-6 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-theme-off-white/60 cursor-pointer hover:bg-theme-muted dark:hover:bg-white/10 group"
                                      onClick={(e) => wk.handleWorkoutSort('exercises', e)}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span>Exercises</span>
                                        <WorkoutSortIcon wk={wk} column="exercises" />
                                      </div>
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-theme-text/70 dark:text-theme-off-white/70 tracking-wider">
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-theme-bg dark:bg-theme-surface divide-y divide-theme-border dark:divide-white/10">
                                  {wk.filteredAndSortedWorkouts.length === 0 ? (
                                    <tr>
                                      <td colSpan={5} className="px-6 py-12 text-center">
                                        <Dumbbell className="w-12 h-12 text-theme-text/50 dark:text-theme-off-white/50 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-theme-text dark:text-theme-off-white mb-2">
                                          {workouts.length === 0 ? 'No workouts yet' : 'No workouts match your filters'}
                                        </h3>
                                        <p className="text-sm text-theme-text/70 dark:text-theme-off-white/70 mb-4">
                                          {workouts.length === 0 
                                            ? 'Get started by creating your first workout'
                                            : 'Try adjusting your filters to see more results'}
                                        </p>
                                      </td>
                                    </tr>
                                  ) : (
                                    wk.filteredAndSortedWorkouts.map((workout, index) => {
                                      const exerciseCount = getWorkoutExerciseCount(workout);
                                      const isLastRow = index === wk.filteredAndSortedWorkouts.length - 1;
                                      const isSelected = selectedWorkouts.has(workout.id);
                                      const workoutIsYou = isTrainerOwnedLibraryRow(workout.createdBy, profileId);
                                      return (
                                        <tr 
                                          key={`workout-${workout.id}`} 
                                          className={`border-b border-theme-border/60 dark:border-white/10 hover:bg-theme-off-white/50 dark:hover:bg-white/5 cursor-pointer ${isSelected ? 'bg-accent-violet/10 dark:bg-accent-lime/10' : ''}`}
                                          onClick={(e) => {
                                            // Don't trigger if a menu is open or clicking on menu-related elements
                                            if (openWorkoutMenu !== null || openWorkoutFilterColumn !== null) {
                                              return;
                                            }
                                            if ((e.target as HTMLElement).closest('.workout-menu-item') || 
                                                (e.target as HTMLElement).closest('.workout-menu') ||
                                                (e.target as HTMLElement).closest('.filter-dropdown') ||
                                                (e.target as HTMLElement).closest('.filter-trigger') ||
                                                (e.target as HTMLElement).closest('[class*="z-[45]"]') ||
                                                (e.target as HTMLElement).closest('[class*="z-[50]"]') ||
                                                (e.target as HTMLElement).closest('[class*="z-[90]"]') ||
                                                (e.target as HTMLElement).closest('[class*="z-[91]"]')) {
                                              return;
                                            }
                                            onWorkoutClick(workout, e);
                                          }}
                                        >
                                          <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() => onSelectWorkout(workout.id)}
                                              className="rounded border-theme-border dark:border-white/10 text-accent-violet dark:text-accent-lime focus:ring-accent-violet dark:focus:ring-accent-lime"
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-normal text-slate-600 dark:text-theme-off-white/80">{workout.name}</div>
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-slate-600 dark:text-theme-off-white/80">
                                            {workout.createdAt ? new Date(workout.createdAt).toLocaleDateString() : 'N/A'}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-slate-600 dark:text-theme-off-white/80">
                                            {exerciseCount} {exerciseCount === 1 ? 'exercise' : 'exercises'}
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium workout-menu" onClick={(e) => e.stopPropagation()}>
                                            <div 
                                              className="relative inline-block"
                                              ref={(el) => {
                                                workoutMenuRefs.current[workout.id] = el;
                                              }}
                                            >
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setOpenWorkoutMenu(openWorkoutMenu === workout.id ? null : workout.id);
                                                }}
                                                className="p-1 text-theme-text/50 dark:text-theme-off-white/50 hover:text-theme-text dark:hover:text-theme-off-white focus:outline-none"
                                              >
                                                <MoreVertical className="w-5 h-5" />
                                              </button>
                                              {openWorkoutMenu === workout.id && (
                                                <>
                                                  <div 
                                                    className="fixed inset-0 z-[90]" 
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setOpenWorkoutMenu(null);
                                                    }}
                                                    onTouchStart={(e) => {
                                                      e.stopPropagation();
                                                    }}
                                                    style={{ touchAction: 'none' }}
                                                  />
                                                  <div 
                                                    className={`absolute right-0 w-48 bg-theme-bg dark:bg-theme-surface rounded-md shadow-lg z-[91] border border-theme-border dark:border-white/10 py-1 workout-menu-item ${
                                                      isLastRow ? 'bottom-full mb-2' : 'mt-2'
                                                    }`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onTouchStart={(e) => e.stopPropagation()}
                                                  >
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        setOpenWorkoutMenu(null);
                                                        onEditWorkout(workout);
                                                      }}
                                                      onTouchEnd={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        setOpenWorkoutMenu(null);
                                                        onEditWorkout(workout);
                                                      }}
                                                      className="block w-full text-left px-4 py-2 text-sm text-theme-text dark:text-theme-off-white hover:bg-theme-muted dark:hover:bg-white/10 flex items-center gap-2 workout-menu-item"
                                                      style={{ touchAction: 'manipulation', pointerEvents: 'auto' }}
                                                    >
                                                      <Edit className="w-4 h-4" />
                                                      Edit
                                                    </button>
                                                    <button
                                                      type="button"
                                                      disabled={trainerWorkoutDuplicateInFlight}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        if (trainerWorkoutDuplicateInFlight) return;
                                                        setOpenWorkoutMenu(null);
                                                        void onDuplicateWorkout(workout);
                                                      }}
                                                      onTouchEnd={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        if (trainerWorkoutDuplicateInFlight) return;
                                                        setOpenWorkoutMenu(null);
                                                        void onDuplicateWorkout(workout);
                                                      }}
                                                      className="block w-full text-left px-4 py-2 text-sm text-theme-text dark:text-theme-off-white hover:bg-theme-muted dark:hover:bg-white/10 flex items-center gap-2 workout-menu-item"
                                                      style={{ touchAction: 'manipulation', pointerEvents: 'auto' }}
                                                    >
                                                      <Copy className="w-4 h-4" />
                                                      Duplicate
                                                    </button>
                                                    {workoutIsYou && (
                                                      <button
                                                        type="button"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          e.preventDefault();
                                                          setOpenWorkoutMenu(null);
                                                          onDeleteWorkout(workout.id, workout.name);
                                                        }}
                                                        className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-theme-muted dark:hover:bg-white/10 flex items-center gap-2 workout-menu-item"
                                                        style={{ touchAction: 'manipulation', pointerEvents: 'auto' }}
                                                      >
                                                        <Trash2 className="w-4 h-4" />
                                                        Delete
                                                      </button>
                                                    )}
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })
                                  )}
                                </tbody>
                              </table>
                              </div>
                            </div>
                          </div>
  );
}
